import React, { useMemo, useState } from 'react';
import { DataGridPremium, GridColDef, GridGroupingColDefOverride } from '@mui/x-data-grid-premium';
import { permissions } from '@retail/auth/data-access';
import { Permission } from '@retail/auth/types';
import { Stack } from '@mui/material';
import { ActionChip } from './ActionChip';
import { TableToolbar } from './TableToolbar';
import { useRowColorGrouping } from '@retail/hooks';
import { Flag } from '@shared/components';

const columns: GridColDef<Permission>[] = [
  {
    field: 'resource',
    headerName: 'Resource',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'orgType',
    headerName: 'Org. type',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'userRole',
    headerName: 'User role',
    flex: 0.8,
    minWidth: 150,
  },
  {
    field: 'appTenants',
    headerName: 'Tenants',
    flex: 0.5,
    minWidth: 80,
    renderCell: ({ row }) => (
      <Stack direction="row" spacing={1}>
        {row.appTenants?.map((tenant) => (
          <Flag country={tenant} key={tenant} size="20px" />
        ))}
      </Stack>
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 0.75,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => {
      if (!row.actions) return null;
      return (
        <Stack direction="row" spacing={0.5}>
          {row.actions.map((action) => (
            <ActionChip key={action} action={action} />
          ))}
        </Stack>
      );
    },
  },
];

const groupingColDef: GridGroupingColDefOverride = {
  minWidth: 150,
  flex: 1,
};

export const PermissionsTable: React.FC = () => {
  const [showSuperuser, setShowSuperuser] = useState(false);
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const filteredPermissions = useMemo(
    () =>
      showSuperuser
        ? permissions
        : permissions.filter((permission) => permission.userRole !== 'SUPERUSER'),
    [showSuperuser]
  );

  return (
    <DataGridPremium<Permission>
      columns={columns}
      rows={filteredPermissions}
      getRowClassName={getRowColorForStripedTable}
      getRowId={({ orgType, resource, userRole }) => `${orgType}-${resource}-${userRole}`}
      disableRowSelectionOnClick
      groupingColDef={groupingColDef}
      showToolbar
      slots={{
        toolbar: (props) => (
          <TableToolbar
            {...props}
            showSuperuser={showSuperuser}
            setShowSuperuser={setShowSuperuser}
          />
        ),
      }}
    />
  );
};
